import {
	forwardRef,
	useCallback,
	useEffect,
	useImperativeHandle,
	useRef,
	useState,
} from "react";
import styles from "./SignatureCanvas.module.css";

export interface SignatureCanvasHandle {
	getDataUrl: () => string | null;
	clear: () => void;
}

interface SignatureCanvasProps {
	placeholderText: string;
	clearLabel: string;
	onChange?: (hasSignature: boolean) => void;
}

export const SignatureCanvas = forwardRef<
	SignatureCanvasHandle,
	SignatureCanvasProps
>(function SignatureCanvas({ placeholderText, clearLabel, onChange }, ref) {
	const canvasRef = useRef<HTMLCanvasElement>(null);
	const isDrawingRef = useRef(false);
	const hasSignatureRef = useRef(false);
	const [hasSignature, setHasSignature] = useState(false);

	useEffect(() => {
		const canvas = canvasRef.current;
		if (!canvas) return;
		const ctx = canvas.getContext("2d");
		if (!ctx) return;

		const dpr = window.devicePixelRatio || 1;
		const rect = canvas.getBoundingClientRect();
		canvas.width = rect.width * dpr;
		canvas.height = rect.height * dpr;
		ctx.scale(dpr, dpr);
		ctx.strokeStyle = "#000000";
		ctx.lineWidth = 2;
		ctx.lineCap = "round";
		ctx.lineJoin = "round";
	}, []);

	const getPos = useCallback((e: PointerEvent): { x: number; y: number } => {
		const canvas = canvasRef.current;
		if (!canvas) return { x: 0, y: 0 };
		const rect = canvas.getBoundingClientRect();
		return {
			x: e.clientX - rect.left,
			y: e.clientY - rect.top,
		};
	}, []);

	const handlePointerDown = useCallback(
		(e: React.PointerEvent<HTMLCanvasElement>) => {
			const canvas = canvasRef.current;
			if (!canvas) return;
			canvas.setPointerCapture(e.pointerId);
			const ctx = canvas.getContext("2d");
			if (!ctx) return;
			isDrawingRef.current = true;
			const { x, y } = getPos(e.nativeEvent);
			ctx.beginPath();
			ctx.moveTo(x, y);
		},
		[getPos],
	);

	const handlePointerMove = useCallback(
		(e: React.PointerEvent<HTMLCanvasElement>) => {
			if (!isDrawingRef.current) return;
			const canvas = canvasRef.current;
			if (!canvas) return;
			const ctx = canvas.getContext("2d");
			if (!ctx) return;
			const { x, y } = getPos(e.nativeEvent);
			ctx.lineTo(x, y);
			ctx.stroke();
			if (!hasSignatureRef.current) {
				hasSignatureRef.current = true;
				setHasSignature(true);
				onChange?.(true);
			}
		},
		[getPos, onChange],
	);

	const handlePointerUp = useCallback(() => {
		isDrawingRef.current = false;
	}, []);

	const handleClear = useCallback(() => {
		const canvas = canvasRef.current;
		if (!canvas) return;
		const ctx = canvas.getContext("2d");
		if (!ctx) return;
		ctx.clearRect(0, 0, canvas.width, canvas.height);
		hasSignatureRef.current = false;
		setHasSignature(false);
		onChange?.(false);
	}, [onChange]);

	useImperativeHandle(
		ref,
		() => ({
			getDataUrl: () => {
				const canvas = canvasRef.current;
				if (!canvas || !hasSignatureRef.current) return null;
				return canvas.toDataURL("image/png");
			},
			clear: handleClear,
		}),
		[handleClear],
	);

	return (
		<div className={styles.wrapper}>
			<div className={styles.canvasArea}>
				{!hasSignature && (
					<p className={styles.placeholder}>{placeholderText}</p>
				)}
				<canvas
					ref={canvasRef}
					className={styles.canvas}
					onPointerDown={handlePointerDown}
					onPointerMove={handlePointerMove}
					onPointerUp={handlePointerUp}
					onPointerLeave={handlePointerUp}
					aria-label={placeholderText}
				/>
			</div>
			<button
				type="button"
				className={styles.clearButton}
				onClick={handleClear}
			>
				{clearLabel}
			</button>
		</div>
	);
});
